/**
 * Expand a leave request into per-day entries, mirroring the backend's
 * expandRequestDays: non-working days (per-user working_days, default
 * Mon–Fri) and public holidays are skipped — they're not consumed from the
 * quota, so the calendar must not show them as leave. Half-day fractions
 * apply on the start/end edges only.
 */

import type { LeaveRequest } from '../../api/leave.api';

export interface ExpandedDay {
  date: string;       // YYYY-MM-DD
  fraction: number;   // 1 or 0.5
}

const DAY_TO_NUM: Record<string, number> = {
  monday: 0, tuesday: 1, wednesday: 2, thursday: 3, friday: 4, saturday: 5, sunday: 6,
};

const DEFAULT_WORKING = new Set([0, 1, 2, 3, 4]); // Mon–Fri

/** Parse a working_days CSV ('monday,tuesday,…') into dow indices (Mon=0..Sun=6). */
export function workingDowSet(csv?: string | null): Set<number> {
  if (!csv || !csv.trim()) return DEFAULT_WORKING;
  const set = new Set<number>();
  for (const part of csv.split(',')) {
    const n = DAY_TO_NUM[part.trim().toLowerCase()];
    if (n !== undefined) set.add(n);
  }
  return set.size ? set : DEFAULT_WORKING;
}

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

function dow(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (dt.getUTCDay() + 6) % 7; // Mon=0..Sun=6
}

export interface ExpandOptions {
  /** Public-holiday dates (YYYY-MM-DD) to exclude. */
  holidays?: Set<string>;
  /** Worked dow indices (Mon=0..Sun=6); defaults to Mon–Fri. */
  workingDays?: Set<number>;
}

export function expandLeaveRequest(req: LeaveRequest, opts: ExpandOptions = {}): ExpandedDay[] {
  const working = opts.workingDays ?? DEFAULT_WORKING;
  const out: ExpandedDay[] = [];
  let cur = req.date_from;
  while (cur <= req.date_to) {
    if (working.has(dow(cur)) && !opts.holidays?.has(cur)) {
      let fraction = 1;
      if (cur === req.date_from && req.half_day_start !== 'full') fraction = 0.5;
      if (cur === req.date_to   && req.half_day_end   !== 'full') fraction = 0.5;
      out.push({ date: cur, fraction });
    }
    cur = addDays(cur, 1);
  }
  return out;
}
